import * as React from 'react'
import { observer } from 'mobx-react'
import OCRScopingJobSidePanelViewModel, {
  OCRScopeWorkStatusFilterType,
} from '../OCRScopingJobSidePanelViewModel'
import {
  SplitViewHeaderSearch,
  SplitViewSidebarListItem,
  SplitViewSidebarListItemTitle,
  SplitViewSidebarListItemArrow,
  SplitViewSidebarListItemSubtitle,
} from 'components/SplitView'
import styles from './OCRScopingJobsListHeader.sass'
import WorkspaceSelectorViewHeading from 'components/WorkspaceSelectorViewHeading'
import Workspace from 'iam/models/Workspace'
import Icon from 'components/Icon'
import { IconButton } from 'components/IconButton'
import Checkbox from '../../../../../../components/Checkbox'

interface OCRScopingJobsListHeaderProps {
  vm: OCRScopingJobSidePanelViewModel
}

export default observer(function OCRScopingJobsListHeader({
  vm,
}: OCRScopingJobsListHeaderProps) {
  return (
    <div className={styles.header}>
      {vm.selectedStatusFilter ? (
        <>
          <SearchWithStatusFilterOCRScopingJobsHeader vm={vm} />
          <ListItemHeader vm={vm} statusFilterType={vm.selectedStatusFilter} />
        </>
      ) : (
        <>
          <AggregationSearchOCRScopingJobsHeader vm={vm} />
          <AggregationListItemHeader />
          {vm.statusFilterOptions.map((statusFilter) => {
            const isDisabled = statusFilter.count() === 0
            const onClick = () =>
              isDisabled ? undefined : vm.setStatusFilter(statusFilter.id)
            return (
              <SplitViewSidebarListItem
                key={statusFilter.id}
                disabled={isDisabled}
                onClick={onClick}
                extras={
                  <SplitViewSidebarListItemCount count={statusFilter.count()} />
                }
              >
                <SplitViewSidebarListItemTitle>
                  {statusFilter.title}
                </SplitViewSidebarListItemTitle>
                <SplitViewSidebarListItemSubtitle>
                  {statusFilter.subtitle}
                </SplitViewSidebarListItemSubtitle>
              </SplitViewSidebarListItem>
            )
          })}
        </>
      )}
    </div>
  )
})

interface SearchOCRScopingJobsHeaderProps {
  vm: OCRScopingJobSidePanelViewModel
}

const SearchWithStatusFilterOCRScopingJobsHeader = observer(
  function SearchWithStatusFilterOCRScopingJobsHeader({
    vm,
  }: SearchOCRScopingJobsHeaderProps) {
    return (
      <div className={styles.searchContainer}>
        {!vm.showSearchField && (
          <div className={styles.searchLeftSide}>
            <IconButton onClick={() => vm.setStatusFilter(undefined)}>
              <Icon name="back" />
            </IconButton>
            <div className={styles.searchTitle}>
              {vm.selectedStatusFilter?.title}
              <span className={styles.searchTitleCount}>
                ({vm.selectedStatusFilter?.count()})
              </span>
            </div>
          </div>
        )}
        {vm.showSearchField && (
          <SplitViewHeaderSearch
            onChange={vm.searchText.change}
            value={vm.searchText.value}
            placeholder="Search jobs"
          />
        )}
        <IconButton onClick={vm.toggleSearchField}>
          <Icon name={vm.showSearchField ? 'close' : 'search'} />
        </IconButton>
      </div>
    )
  },
)

const AggregationSearchOCRScopingJobsHeader = observer(
  function AggregationSearchOCRScopingJobsHeader({
    vm,
  }: SearchOCRScopingJobsHeaderProps) {
    return (
      <div
        className={
          vm.canManageOCRScopingJobs
            ? styles.searchWithWorkspaceFilterContainer
            : styles.searchWithOutWorkspaceFilterContainer
        }
      >
        {vm.canManageOCRScopingJobs ? (
          <WorkspaceSelectorViewHeading
            primaryText={null}
            allOptionDropdownItem={<>All workspaces</>}
            alwaysUseDropdown
            workspaces={vm.workspaces}
            dropdownTitle="Select workspace"
            onChange={vm.selectWorkspaceForFiltering}
            searcher={vm.workspaceSelectorViewModel}
            renderName={(workspace) => {
              return <>{workspace.name}</>
            }}
            allOptionText={'All workspaces'}
            value={
              vm.workspaceFilter.type === 'AllWorkspaces'
                ? Workspace.none
                : vm.workspaceFilter.workspace
            }
            viewHeadingSize="small"
            showClearButton
          />
        ) : (
          <span>OCR job scope</span>
        )}
      </div>
    )
  },
)

const ListItemHeader = observer(function ListItemHeader({
  vm,
  statusFilterType,
}: {
  vm: OCRScopingJobSidePanelViewModel
  statusFilterType: OCRScopeWorkStatusFilterType
}) {
  const ocrScopingJobBulkActionViewModel = vm.ocrScopingJobBulkActionViewModel
  const checked =
    ocrScopingJobBulkActionViewModel.hasSomeItemsSelectedForBulkAction
      ? 'indeterminate'
      : ocrScopingJobBulkActionViewModel.hasAllItemsSelectedForBulkAction
  return (
    <div className={styles.listHeader}>
      <span className={styles.listHeaderAction}>
        {statusFilterType.bulkAction && (
          <Checkbox
            checked={checked}
            onChange={
              ocrScopingJobBulkActionViewModel.toggleSelectAllItemForBulkAction
            }
          />
        )}
      </span>
      <span>{statusFilterType.headerName}</span>
    </div>
  )
})

const AggregationListItemHeader = observer(
  function AggregationListItemHeader() {
    return <div className={styles.aggregationListHeader}>Status</div>
  },
)

const SplitViewSidebarListItemCount = observer(
  function SplitViewSidebarListItemCount({ count }: { count: number }) {
    return (
      <div className={styles.listCounter}>
        <span>{count}</span>
        <SplitViewSidebarListItemArrow />
      </div>
    )
  },
)
